import { useEffect } from 'react'

// Type definitions
type EventCallback = (data?: any) => void
type EventSubscribers = Map<string, Set<EventCallback>>

// Global event bus instance
const subscribers: EventSubscribers = new Map()

// Dispatch function for publishing events
export const dispatch = (eventName: string, data?: any) => {
	const callbacks = subscribers.get(eventName)
	if (callbacks) {
		callbacks.forEach((callback) => callback(data))
	}
}

// Hook for subscribing to events
export const useBus = (eventName: string, callback: EventCallback, deps: any[] = []) => {
	useEffect(() => {
		// Create a new Set if this is the first subscriber
		if (!subscribers.has(eventName)) {
			subscribers.set(eventName, new Set())
		}

		// Add the callback to the subscribers
		const callbacks = subscribers.get(eventName)!
		callbacks.add(callback)

		// Cleanup function to remove the subscription
		return () => {
			const callbacks = subscribers.get(eventName)
			if (callbacks) {
				callbacks.delete(callback)
				if (callbacks.size === 0) {
					subscribers.delete(eventName)
				}
			}
		}
	}, [eventName, ...deps])
}

// Optional: Type-safe versions
interface EventMap {
	[key: string]: any | undefined
}

export const dispatchTyped = <T extends keyof EventMap>(eventName: T, data?: EventMap[T]) => {
	dispatch(eventName as string, data)
}

export const useBusTyped = <T extends keyof EventMap>(
	eventName: T,
	callback: (data?: EventMap[T]) => void,
	deps: any[] = [],
) => {
	useBus(eventName as string, callback, deps)
}
